'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ProjectRecord } from '@/lib/projects/project-types';
import { formatRelativeTime } from '@/lib/projects/project-utils';
import { ROUTES } from '@/lib/routes';

interface RecentProjectsCardProps {
  projects: ProjectRecord[];
  count?: number;
}

export const RecentProjectsCard = React.memo(function RecentProjectsCard({ projects, count = 5 }: RecentProjectsCardProps) {
  const recent = useMemo(() => {
    return [...projects]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, count);
  }, [projects, count]);

  return (
    <Card>
      <CardContent className="pt-0">
        <h3 className="text-base font-medium mb-4">Recently Updated</h3>
        {recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent activity</p>
        ) : (
          <div className="space-y-3">
            {recent.map((p) => (
              <div
                key={p.id}
                className="flex items-start gap-3 rounded-md border bg-muted/20 px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{p.projectName}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {p.organizationName}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    {p.sourceDomain}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {formatRelativeTime(p.updatedAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        <Link
          href={ROUTES.projects}
          className="inline-block text-xs text-primary hover:underline mt-3"
        >
          View all projects →
        </Link>
      </CardContent>
    </Card>
  );
});
